import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useLogging } from '@/shared/hooks/useLogging';
import { toast } from '@/hooks/use-toast';

interface AuthRecoveryOptions {
  maxRetries?: number;
  onRecoverySuccess?: () => void;
  onRecoveryFailure?: () => void;
}

export function useAuthRecovery(options: AuthRecoveryOptions = {}) {
  const { maxRetries = 2, onRecoverySuccess, onRecoveryFailure } = options;
  const { refreshProfile, signOut, tenantId, user } = useAuth();
  const { error: logError, info: logInfo } = useLogging('AuthRecovery');
  const { t } = useTranslation('auth');
  const recoveryAttempts = useRef(0);
  const isRecovering = useRef(false);

  const isAuthError = useCallback((error: string): boolean => {
    const authErrorPatterns = [
      'Tenant not found or invalid',
      'Access denied to tenant',
      'Invalid tenant schema',
      'No tenant user profile found'
    ];
    
    return authErrorPatterns.some(pattern => 
      error.toLowerCase().includes(pattern.toLowerCase())
    );
  }, []);

  const attemptRecovery = useCallback(async (
    originalError: string,
    contextInfo?: Record<string, unknown>
  ): Promise<boolean> => {
    // Prevent concurrent recovery attempts
    if (isRecovering.current) {
      logInfo('Recovery already in progress, skipping duplicate attempt');
      return false;
    }

    // Check retry limit
    if (recoveryAttempts.current >= maxRetries) {
      logError('Maximum recovery attempts reached', {
        attempts: recoveryAttempts.current,
        maxRetries,
        originalError,
        userId: user?.id,
        tenantId,
        ...contextInfo
      });
      
      toast({
        title: t('recovery.errorTitle'),
        description: t('recovery.errorDescription')
      });
      
      onRecoveryFailure?.();
      
      // Force sign out after max retries
      setTimeout(() => {
        signOut();
      }, 1000);
      
      return false;
    }

    isRecovering.current = true;
    recoveryAttempts.current += 1;

    logInfo('Attempting authentication recovery', {
      attempt: recoveryAttempts.current,
      maxRetries,
      originalError,
      userId: user?.id,
      tenantId,
      ...contextInfo
    });

    // Show recovery message to user
    const recoveryToast = toast({
      title: t('recovery.issueDetectedTitle'),
      description: t('recovery.issueDetectedDescription')
    });

    try {
      // Attempt to refresh the user profile and tenant information
      await refreshProfile();
      
      // Give some time for the profile refresh to complete
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      logInfo('Authentication recovery successful', {
        attempt: recoveryAttempts.current,
        userId: user?.id,
        tenantId,
        ...contextInfo
      });

      // Update toast with success message
      recoveryToast.update({
        id: recoveryToast.id,
        title: t('recovery.recoveredTitle'),
        description: t('recovery.recoveredDescription')
      });

      // Reset retry counter on success
      recoveryAttempts.current = 0;
      onRecoverySuccess?.();
      
      return true;
    } catch (recoveryError) {
      logError('Authentication recovery failed', {
        attempt: recoveryAttempts.current,
        maxRetries,
        originalError,
        recoveryError: recoveryError instanceof Error ? recoveryError.message : String(recoveryError),
        userId: user?.id,
        tenantId,
        ...contextInfo
      });

      // Update toast with failure message
      recoveryToast.update({
        id: recoveryToast.id,
        title: t('recovery.failedTitle'),
        description: recoveryAttempts.current >= maxRetries
          ? t('recovery.failedDescriptionFinal', { current: recoveryAttempts.current, max: maxRetries })
          : t('recovery.failedDescriptionRetry', { current: recoveryAttempts.current, max: maxRetries })
      });

      return false;
    } finally {
      isRecovering.current = false;
    }
  }, [
    maxRetries, 
    refreshProfile, 
    signOut, 
    user?.id, 
    tenantId, 
    logError, 
    logInfo, 
    onRecoverySuccess, 
    onRecoveryFailure
  ]);

  const handleError = useCallback(async (
    error: string,
    contextInfo?: Record<string, unknown>
  ): Promise<boolean> => {
    // Only attempt recovery for authentication-related errors
    if (!isAuthError(error)) {
      return false;
    }

    return await attemptRecovery(error, contextInfo);
  }, [isAuthError, attemptRecovery]);

  const resetRecovery = useCallback(() => {
    recoveryAttempts.current = 0;
    isRecovering.current = false;
  }, []);

  return {
    handleError,
    resetRecovery,
    isRecovering: isRecovering.current,
    currentAttempts: recoveryAttempts.current,
    maxRetries
  };
}